import { normalizePhotoName } from "./data.jsx";
import { BASE_ROW } from "./theme.jsx";

const WEEKDAYS = [
  { id: "sun", label: "อาทิตย์", labelEn: "Sunday", short: "อา", shortEn: "Sun", name: "ศึกเกียรติเพชร TKO", category: "Promoter", code: "KP_TKO" },
  { id: "mon", label: "จันทร์", labelEn: "Monday", short: "จ", shortEn: "Mon", name: "ราชดำเนินน็อคเอาท์", category: "RKO", code: "RKO_Mon" },
  { id: "tue", label: "อังคาร", labelEn: "Tuesday", short: "อ", shortEn: "Tue", name: "ราชดำเนินน็อคเอาท์", category: "RKO", code: "RKO_Tue" },
  { id: "wed", label: "พุธ", labelEn: "Wednesday", short: "พ", shortEn: "Wed", name: "ศึกมวยไทยพลังใหม่", category: "Promoter", code: "New_Power" },
  { id: "thu", label: "พฤหัสบดี", labelEn: "Thursday", short: "พฤ", shortEn: "Thu", name: "ศึกเพชรยินดี", category: "Promoter", code: "PYD" },
  { id: "fri", label: "ศุกร์", labelEn: "Friday", short: "ศ", shortEn: "Fri", name: "ราชดำเนินน็อคเอาท์", category: "RKO", code: "RKO_Fri" },
  { id: "sat", label: "เสาร์", labelEn: "Saturday", short: "ส", shortEn: "Sat", name: "ราชดำเนินเวิลด์ซีรีส์", category: "RWS", code: "RWS" },
];
const weekdayOf = (date) => WEEKDAYS[date.getDay()];

function pad(n) { return String(n).padStart(2, "0"); }
function toKey(d) { return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`; }
function fromKey(k) { const [y, m, d] = k.split("-").map(Number); return new Date(y, m - 1, d); }
function thaiDate(d, lang) {
  if (lang === "en") {
    const days = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
    const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    return `${days[d.getDay()]}, ${months[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}`;
  }
  const days = ["อาทิตย์", "จันทร์", "อังคาร", "พุธ", "พฤหัสบดี", "ศุกร์", "เสาร์"];
  const months = ["ม.ค.", "ก.พ.", "มี.ค.", "เม.ย.", "พ.ค.", "มิ.ย.", "ก.ค.", "ส.ค.", "ก.ย.", "ต.ค.", "พ.ย.", "ธ.ค."];
  return `วัน${days[d.getDay()]}ที่ ${d.getDate()} ${months[d.getMonth()]} ${d.getFullYear() + 543}`;
}
const uid = () => `x${Date.now()}${Math.random().toString(36).slice(2, 7)}`;
function newMasterCue() { return { id: uid(), name: "", icon: "glove", color: "black", bg: "", boutNumber: "", redCorner: "", blueCorner: "" }; }
// short label for a master cue used wherever space is tight (the stage-panel
// "assign to bout" dropdown): "3. #3 Somchai vs Somsak — คู่เอก" when bout
// info is set, prepended ahead of the plain cue name (never dropping it) so
// non-fight cues (ยกที่ 1, พักยก, ประกาศผล…) still show something sensible.
function masterCueLabel(cue, idx, t) {
  const info = [];
  if (cue.boutNumber) info.push(`#${cue.boutNumber}`);
  if (cue.redCorner || cue.blueCorner) info.push(`${cue.redCorner || "?"} vs ${cue.blueCorner || "?"}`);
  const base = cue.name || t("untitledCue");
  return info.length ? `${idx + 1}. ${info.join(" ")} — ${base}` : `${idx + 1}. ${base}`;
}
function newBlock(start) { return { id: uid(), start, span: 1, sound: "", image: "", color: "warmgray", bg: "" }; }
// A "stage" is a Wai Kru / During Fight entry (P1, P2, B1…B6, etc.) that can be
// linked to one or more master cues (fight rows) by each cue's stable id —
// never by array index, since cues can be reordered/deleted (see remapIds /
// deleteMasterCue, which keep this link consistent). Older saved stages only
// ever had a single `assignedCueId` string; stageCueIds() reads either shape
// so legacy boards keep working, and every write goes through the new
// `assignedCueIds` array field going forward.
function newStage() { return { id: uid(), stage: "", waikruText: "", waikruBg: "", duringFightText: "", duringFightBg: "", sound: "", assignedCueIds: [] }; }
// A stage covers a RANGE of master cues — cueRange: { from, to } by cue id, so
// cues inserted inside the bout later are covered too — or, for stages linked
// before ranges existed, a list of cue ids. Pass masterCues to resolve ranges.
function stageRows(stage, masterCues) {
  const r = stage && stage.cueRange;
  if (!r || !masterCues) return null;
  const a = masterCues.findIndex((m) => m.id === r.from);
  const b = masterCues.findIndex((m) => m.id === r.to);
  if (a === -1 || b === -1) return null;
  return a <= b ? [a, b] : [b, a];
}
function stageCueIds(stage, masterCues) {
  const rows = stageRows(stage, masterCues);
  if (rows) return masterCues.slice(rows[0], rows[1] + 1).map((m) => m.id);
  return stage.assignedCueIds || (stage.assignedCueId ? [stage.assignedCueId] : []);
}
// After master cues are deleted: a range end that was deleted moves inward to
// the nearest cue that's left (no cue left -> no range); listed ids are dropped.
function dropCuesFromStage(stage, deleted, masterCues) {
  let next = stage;
  const rows = stageRows(stage, masterCues);
  if (rows) {
    const kept = masterCues.slice(rows[0], rows[1] + 1).filter((m) => !deleted.has(m.id));
    const { cueRange, ...rest } = stage;
    const [from, to] = kept.length ? [kept[0].id, kept[kept.length - 1].id] : [];
    if (!kept.length) next = rest;
    else if (from !== masterCues[rows[0]].id || to !== masterCues[rows[1]].id) next = { ...stage, cueRange: { from, to } };
  }
  const ids = next.assignedCueIds || (next.assignedCueId ? [next.assignedCueId] : []);
  return ids.some((id) => deleted.has(id)) ? { ...next, assignedCueIds: ids.filter((id) => !deleted.has(id)) } : next;
}
// Where the fighting starts inside a stage's cues (its first "ยกที่ 1"):
// before it is Wai Kru, from it on During Fight. -1 = no rounds in the range.
function stageFightStart(stage, masterCues) {
  const rows = stageRows(stage, masterCues);
  if (!rows) return -1;
  for (let i = rows[0]; i <= rows[1]; i++) if (/ยกที่\s*1(?!\d)/.test(String(masterCues[i].name || ""))) return i;
  return -1;
}
// Finds each bout's rows in a rundown, in show order: [{ from, to }] (indexes).
// A fight is a "ยกที่ 1 … ประกาศผล" run of rounds, or one "… Bout N" cue with
// no rounds of its own (a prelim shown as a single cue). It starts at the
// first "คู่ที่ N" / "Bout" / "คู่เอก" / "เปิดมวย" cue after the previous bout,
// else at its first "เปิดตัว…" cue (with an "introducing" line just before it).
const BOUT_LABEL = /คู่ที่\s*\d|คู่\s*\d|\bbout\b|คู่เอก|เปิดคู่|เปิดมวย/i;
function detectBouts(masterCues) {
  const names = (masterCues || []).map((m) => String(m.name || ""));
  const marks = [];
  names.forEach((n, i) => {
    if (/ยกที่\s*1(?!\d)/.test(n)) marks.push({ i, rounds: true });
    else if (/\bbout\b/i.test(n)) marks.push({ i, rounds: false });
  });
  // a "Bout" cue with its rounds right after it (within 3 rows) is one fight
  const fights = marks.filter((f, k) => f.rounds || !(marks[k + 1] && marks[k + 1].rounds && marks[k + 1].i - f.i <= 3));
  const out = [];
  let prevEnd = -1;
  fights.forEach((f, k) => {
    const next = fights[k + 1] ? fights[k + 1].i : names.length;
    let end = f.i;
    if (f.rounds) {
      const r = names.slice(f.i, next).findIndex((n) => /ประกาศผล/.test(n));
      end = r !== -1 ? f.i + r : next - 1;
    }
    const between = [];
    for (let i = prevEnd + 1; i < f.i; i++) between.push(i);
    let start = between.find((i) => BOUT_LABEL.test(names[i]));
    if (start === undefined && f.rounds) {
      const intro = between.find((i) => /เปิดตัว/.test(names[i]));
      if (intro !== undefined) start = intro - 1 > prevEnd && /introduc|ladies and gentlemen/i.test(names[intro - 1]) ? intro - 1 : intro;
    }
    out.push({ from: start === undefined ? f.i : start, to: end });
    prevEnd = end;
  });
  return out;
}

// ---- Wai Kru / During Fight presets, transcribed from the RWS / RKO / Promoter
// "immersive rundown" reference sheets. These are read-only reference data (not
// stored per-board) — picking one in the stage panel copies its text fields onto
// a stage row so that row can be "synced" to whichever bout it's assigned to.
// Deliberately never touches waikruBg/duringFightBg: a row that already has a
// photo attached keeps it when synced/re-synced.
const STAGE_PRESETS = {
  // RWS — Knocktober
  RWS: [
    { pos: "P1", waikruText: "ยันต์", duringFightText: "War", sound: "During Fight: มีเสียง 1 ยก *แค่ยกแรก" },
    { pos: "P2", waikruText: "ฟ้อนเจิง", duringFightText: "New Rat", sound: "" },
    { pos: "B1", waikruText: "ทศกัณฐ์ชุบตัว", duringFightText: "ทศกัณฐ์ยกเขาไกรลาส", sound: "Wai Kru: มีเสียง\nDuring Fight: มีเสียง 1 ยก *แค่ยกแรก" },
    { pos: "B2", waikruText: "Gold Lotus", duringFightText: "หนุมานอมพลับพลา", sound: "During Fight: มีเสียง" },
    { pos: "B3", waikruText: "เอราวัณ", duringFightText: "นารายณ์กวนเกษียรสมุทร", sound: "" },
    { pos: "B4", waikruText: "Hall of Fame", duringFightText: "Hall of Fame", sound: "" },
    { pos: "B5", waikruText: "รัตนโกสินทร์ (บัว)", duringFightText: "Paradise", sound: "During Fight: มีเสียง 3 ยก *3 ชั้น" },
  ],
  // RKO — update opt2
  RKO: [
    { pos: "P1", waikruText: "ยันต์", duringFightText: "New War", sound: "During Fight: มีเสียง 1 ยก *แค่ยกแรก" },
    { pos: "P2", waikruText: "ฟ้อนเจิง", duringFightText: "New Rat", sound: "" },
    { pos: "B1", waikruText: "Forest", duringFightText: "ปี่กลอง", sound: "" },
    { pos: "B2", waikruText: "รัตนโกสินทร์ (บัว)", duringFightText: "Paradise", sound: "During Fight: มีเสียง 3 ยก *3 ชั้น" },
    { pos: "B3", waikruText: "ทศกัณฐ์ชุบตัว", duringFightText: "ทศกัณฐ์ยกเขาไกรลาส", sound: "Wai Kru: มีเสียง\nDuring Fight: มีเสียง 1 ยก *แค่ยกแรก" },
    { pos: "B4", waikruText: "Gold Lotus", duringFightText: "หนุมานอมพลับพลา", sound: "During Fight: มีเสียง" },
    { pos: "B5", waikruText: "เอราวัณ", duringFightText: "นารายณ์กวนเกษียรสมุทร", sound: "" },
  ],
  // Promoter — 9 bouts
  Promoter: [
    { pos: "B1", waikruText: "RAJ", duringFightText: "RAJ", sound: "" },
    { pos: "B2", waikruText: "RAJ", duringFightText: "RAJ", sound: "" },
    { pos: "B3", waikruText: "Forest", duringFightText: "ปี่กลอง", sound: "" },
    { pos: "B4", waikruText: "ยันต์", duringFightText: "New War", sound: "During Fight: มีเสียง 1 ยก *แค่ยกแรก" },
    { pos: "B5", waikruText: "ฟ้อนเจิง", duringFightText: "New Rat", sound: "" },
    { pos: "B6", waikruText: "รัตนโกสินทร์ (บัว)", duringFightText: "Paradise", sound: "During Fight: มีเสียง 3 ยก *3 ชั้น" },
    { pos: "B7", waikruText: "ทศกัณฐ์ชุบตัว", duringFightText: "ทศกัณฐ์ยกเขาไกรลาส", sound: "Wai Kru: มีเสียง\nDuring Fight: มีเสียง 1 ยก *แค่ยกแรก" },
    { pos: "B8", waikruText: "Gold Lotus", duringFightText: "หนุมานอมพลับพลา", sound: "During Fight: มีเสียง" },
    { pos: "B9", waikruText: "เอราวัณ", duringFightText: "นารายณ์กวนเกษียรสมุทร", sound: "" },
  ],
  // New Power — 9 bouts (B1–B4 have no immersive scene)
  "New Power": [
    { pos: "B5", waikruText: "ยันต์", duringFightText: "New War", sound: "During Fight: มีเสียง 1 ยก *แค่ยกแรก" },
    { pos: "B6", waikruText: "ฟ้อนเจิง", duringFightText: "New Rat", sound: "" },
    { pos: "B7", waikruText: "ทศกัณฐ์ชุบตัว", duringFightText: "ทศกัณฐ์ยกเขาไกรลาส", sound: "Wai Kru: มีเสียง\nDuring Fight: มีเสียง 1 ยก *แค่ยกแรก" },
    { pos: "B8", waikruText: "Gold Lotus", duringFightText: "หนุมานอมพลับพลา", sound: "During Fight: มีเสียง" },
    { pos: "B9", waikruText: "เอราวัณ", duringFightText: "นารายณ์กวนเกษียรสมุทร", sound: "" },
  ],
  // Kiatpetch — 10 bouts (B1–B4 have no immersive scene)
  Kiatpetch: [
    { pos: "B5", waikruText: "ยันต์", duringFightText: "New War", sound: "During Fight: มีเสียง 1 ยก *แค่ยกแรก" },
    { pos: "B6", waikruText: "ฟ้อนเจิง", duringFightText: "New Rat", sound: "" },
    { pos: "B7", waikruText: "ปี่กลอง", duringFightText: "Paradise", sound: "During Fight: มีเสียง 3 ยก *3 ชั้น" },
    { pos: "B8", waikruText: "ทศกัณฐ์ชุบตัว", duringFightText: "ทศกัณฐ์ยกเขาไกรลาส", sound: "Wai Kru: มีเสียง\nDuring Fight: มีเสียง 1 ยก *แค่ยกแรก" },
    { pos: "B9", waikruText: "Gold Lotus", duringFightText: "หนุมานอมพลับพลา", sound: "During Fight: มีเสียง" },
    { pos: "B10", waikruText: "เอราวัณ", duringFightText: "นารายณ์กวนเกษียรสมุทร", sound: "" },
  ],
};
// which preset a board opens on: by its show code first, then its category
const STAGE_PRESET_BY_CODE = { KP_TKO: "Kiatpetch", New_Power: "New Power", PYD: "Promoter" };
const STAGE_PRESET_CATEGORIES = Object.keys(STAGE_PRESETS);
function findStagePreset(category, pos) {
  return (STAGE_PRESETS[category] || []).find((p) => p.pos === pos) || null;
}
function newStageFromPreset(category, pos) {
  const p = findStagePreset(category, pos);
  if (!p) return newStage();
  return { ...newStage(), stage: p.pos, waikruText: p.waikruText, duringFightText: p.duringFightText, sound: p.sound };
}

function emptyBoard() { return { name: "", category: "", code: "", masterCues: [], onGround: [], broadcastMain: [], platforms: {}, stages: [] }; }
function withBoardDefaults(b) { return { ...emptyBoard(), ...b }; }
function getBlocksFromDay(d, col) {
  if (col === "onGround") return d.onGround;
  if (col === "broadcastMain") return d.broadcastMain;
  return d.platforms[col] || [];
}
function computeRowHeights(len, columnsBlocks) {
  const heights = [];
  for (let i = 0; i < len; i++) {
    let max = 1;
    for (const blocks of columnsBlocks) { const c = blocks.filter((b) => b.start === i).length; if (c > max) max = c; }
    heights.push(BASE_ROW * max);
  }
  return heights;
}
function cumulative(heights) { const c = [0]; for (let i = 0; i < heights.length; i++) c.push(c[i] + heights[i]); return c; }
function coveredRows(blocks) { const s = new Set(); blocks.forEach((b) => { for (let r = b.start; r < b.start + b.span; r++) s.add(r); }); return s; }
function layoutBlocks(blocks, heights, cum) {
  const groups = {};
  blocks.forEach((b) => { (groups[b.start] = groups[b.start] || []).push(b); });
  const out = [];
  Object.entries(groups).forEach(([startStr, group]) => {
    const start = Number(startStr);
    if (group.length === 1) {
      const b = group[0];
      const end = Math.min(b.start + b.span, heights.length);
      out.push({ block: b, top: cum[start], height: cum[end] - cum[start] });
    } else {
      const rowH = heights[start] / group.length;
      group.forEach((b, i) => out.push({ block: b, top: cum[start] + i * rowH, height: rowH }));
    }
  });
  return out;
}
function reindexAfterDelete(blocks, idx) {
  const out = [];
  for (const b of blocks) {
    const end = b.start + b.span - 1;
    if (idx < b.start) out.push({ ...b, start: b.start - 1 });
    else if (idx > end) out.push({ ...b });
    else { const span = b.span - 1; if (span > 0) out.push({ ...b, span }); }
  }
  return out;
}
function remapIds(board) {
  const remap = (arr) => (arr || []).map((b) => ({ ...b, id: uid() }));
  const oldCues = board.masterCues || [];
  const newCues = oldCues.map((m) => ({ ...m, id: uid() }));
  // blocks reference a master cue by array position (start), so those don't
  // need remapping — but stages reference one by stable id, so build an
  // old-id → new-id map and carry that link across, dropping it if the
  // referenced cue somehow isn't found rather than pointing at a stale id.
  const idMap = {};
  oldCues.forEach((m, i) => { idMap[m.id] = newCues[i].id; });
  const remapStages = (arr) => (arr || []).map((s) => ({ ...s, id: uid(), assignedCueIds: stageCueIds(s).map((cid) => idMap[cid]).filter(Boolean) }));
  return {
    ...board,
    masterCues: newCues,
    onGround: remap(board.onGround), broadcastMain: remap(board.broadcastMain),
    platforms: Object.fromEntries(Object.entries(board.platforms || {}).map(([k, v]) => [k, remap(v)])),
    stages: remapStages(board.stages),
  };
}

// ---- column-C (Rundown) extracted from RWS sheet, tab "16 กย" (LIVE section only) ----
const RWS_16SEP_CUES = ["Title RWS เข้ารายการ", "ภาพหน้าสนาม + ภาพในสนาม + คนดูในสนาม", "เปิดรายการ พิธีกร 3 คน เข้าสู่ RWS TOURNAMENT 2023", "พิธีกร 3 คน แนะนำมวยทั้งรายการ Fight card", "อองตวนโยนเปิดตัวมวย", "เปิดตัวมุมแดง", "เดินแอ็คชั่น เข้ามุม", "เปิดตัวมุมน้ำเงิน", "เดินแอ็คชั่น เข้ามุม", "ตอนนักมวยคู่ที่ 1 ไหว้ครู (พี่เบียร์พูดบรรยายเรื่องการไหว้ครู) ตัด ins สลับ ชาวต่างชาติ", "(บนเวที) พี่เบียร์ขึ้นเวที This is Muay Thai This is Ratchadamnern", "พี่เบียร์พูดข้อมูลนักมวย (มีคำลูกค้า CO-Sponsor 3 M-150)", "แนะนำกรรมการ แนะนำแดงและแนะนำน้ำเงิน", "แข่งขันมวย RWS คู่ที่ 1", "ยกที่ 1", "พักยก", "ยกที่ 2", "พักยก", "ยกที่ 3", "หมดยก 3 รวมคะแนนเข้าสโลว์", "ประกาศผล", "ภาพ Insert : Kota และ Joker เดินทางถึงสนาม (เวลาจริง 19.00 น.)", "ออกเบรก", "พิธีกร 3 คน recap มวยคู่ที่ 1 แนะนำมวยคู่ที่ 2", "อองตวนโยนเปิดตัวมวย", "เปิดตัวมุมแดง", "เดินแอ็คชั่น เข้ามุม", "เปิดตัวมุมน้ำเงิน", "เดินแอ็คชั่น เข้ามุม", "ไหว้ครู", "พี่เบียร์พูดข้อมูลนักมวย (มีคำลูกค้า CO-Sponsor 2 ปุ้ยหัววัว)", "แข่งขันมวย RWS คู่ที่ 2", "ยกที่ 1", "พักยก", "ยกที่ 2", "พักยก", "ยกที่ 3", "ประกาศผล", "ภาพชั่งน้ำหนักคู่ต่อไป", "ออกเบรก", "พิธีกร 3 recap มวยคู่ที่ 2 แนะนำมวยคู่ 3", "อองตวนโยนเปิดตัวมวย", "เปิดตัวมุมแดง", "เดินแอ็คชั่น เข้ามุม", "เปิดตัวมุมน้ำเงิน", "เดินแอ็คชั่น เข้ามุม", "ไหว้ครู", "พี่เบียร์พูดข้อมูลนักมวย (มีคำลูกค้า Co-Sponsor 1 น้ำมันเครื่อง Trane)", "แข่งขันมวย RWS คู่ที่ 3", "ยกที่ 1", "พักยก", "ยกที่ 2", "พักยก", "ยกที่ 3", "ประกาศผล", "ภาพชั่งน้ำหนักคู่ต่อไป", "ออกเบรก (เล่นกิจกรรมแจกเสื้อ 10 รางวัลใน Onground)", "พิธีกร 3 คน recap มวยคู่ที่ 3 แนะนำมวยคู่ที่ 4", "อองตวนโยนเปิดตัวมวยทั้งคู่", "VTR คู่เอก", "เปิดตัวมุมแดง", "เดินแอ็คชั่น เข้ามุม", "เปิดตัวมุมน้ำเงิน", "เดินแอ็คชั่น เข้ามุม", "ไหว้ครู", "พี่เบียร์พูดข้อมูลนักมวย", "แข่งขันมวย RWS คู่ที่ 4", "ยกที่ 1", "พักยก", "ยกที่ 2", "พักยก", "ยกที่ 3", "ประกาศผล", "ภาพห้องแต่งตัว Kota และ Joker", "ออกเบรก", "พิธีกร 3 คน : Kick boxing (ชก 3 พัก 2)", "อองตวนโยนเปิดตัวมวย Kota vs Joker", "Teaser", "เปิดตัวมุมน้ำเงิน Joker", "เดินแอ็คชั่น เข้ามุม", "เปิดตัวมุมแดง Kota", "เดินแอ็คชั่น เข้ามุม", "*ไม่มีการไหว้ครู*", "พี่เบียร์พูดข้อมูลนักมวย", "แข่งขันมวย RWS คู่ที่ 5 Kota vs Joker", "ยกที่ 1", "พักยก", "ยกที่ 2", "พักยก", "ยกที่ 3", "ประกาศผล", "ออกเบรก", "พิธีกร 3 คน สรุปผลการแข่งขันของวันนี้", "ขึ้น Super: (Plate ค้างไว้) ติดตามชมกันต่อ FB/Youtube Workpoint", "ตารางคะแนน Super Welterweight (154 lbs) A-B", "Bracket อัพเดทโปรแกรม Super Welterweight (154 lbs) A-B", "ขึ้น Fight Card RWS Special Event ครั้งถัดไป", "ขึ้น VTR ตารางไฟต์ต่อไป (UPCOMING SCHEDULE)", "ลารายการ"];
function guessIcon(text) {
  if (/ยกที่|พักยก/.test(text)) return "bell";
  if (/ไหว้ครู/.test(text)) return "glove";
  if (/แข่งขันมวย/.test(text)) return "fist";
  if (/ประกาศผล|ตารางคะแนน|Bracket/.test(text)) return "belt";
  if (/VTR|Title|Teaser|Super/.test(text)) return "flame";
  if (/พิธีกร|อองตวน|พี่เบียร์/.test(text)) return "mic";
  if (/ออกเบรก/.test(text)) return "gong";
  return "corner";
}
// ---- bulk text paste: one line -> one master cue ----
// Optional leading marker sets the cue's color, mirroring the row-type idea
// from the older Firebase rundown tool, adapted to this app's color field
// since master cues here don't have a separate "type".
function parseBulkCueLine(raw) {
  let line = String(raw || "").trim();
  if (!line) return null;
  // "#12 ชื่อคิว" (also "#12:", "#12." or "#12)") targets row 12 exactly — that
  // row's text is replaced instead of a new cue being added. Only "#" counts,
  // so times like "18.00 Welcome" or "19:10" are never read as a row number.
  let row = 0;
  const target = line.match(/^#\s*(\d{1,4})\s*[:.)\-]?\s*/);
  if (target) { row = parseInt(target[1], 10); line = line.slice(target[0].length).trim(); }
  let color = "warmgray";
  const before = line;
  if (line.startsWith("//")) { color = "slate"; line = line.slice(2).trim(); }
  else if (line.startsWith("👉")) { color = "orange"; line = line.slice(2).trim(); }
  else if (line.startsWith("*")) { color = "red"; line = line.slice(1).trim(); }
  else if (line.startsWith(">")) { color = "mustard"; line = line.slice(1).trim(); }
  else if (line.startsWith("-")) { color = "warmgray"; line = line.slice(1).trim(); }
  if (!line) return null;
  return { name: line, color, icon: guessIcon(line), boutNumber: detectBoutNumber(line), row, marked: line !== before };
}
// Decide where each parsed bulk line lands, without touching the board:
//   replace: Map(rowIndex -> lineIndex) for rows whose text gets overwritten
//   add:     line indexes that become new cues, all inserted at addAt
//   missing: "#N" row numbers that don't exist (those lines are added instead)
// mode "append" adds at the end, "insert" adds before row `at` (0-based),
// "replace" overwrites rows from `at` downward and adds past the last row.
function planBulkPaste(parsed, rowCount, mode, at) {
  const replace = new Map();
  const add = [];
  const missing = [];
  const free = [];
  (parsed || []).forEach((c, k) => {
    if (c.row) {
      if (c.row >= 1 && c.row <= rowCount) replace.set(c.row - 1, k);
      else { missing.push(c.row); add.push(k); }
    } else free.push(k);
  });
  const start = Math.max(0, Math.min(Number(at) || 0, rowCount));
  let addAt = rowCount;
  if (mode === "insert") addAt = start;
  if (mode === "replace") {
    let p = start;
    for (const k of free) {
      while (p < rowCount && replace.has(p)) p++;
      if (p < rowCount) { replace.set(p, k); p++; } else add.push(k);
    }
  } else add.push(...free);
  add.sort((a, b) => a - b);
  return { replace: new Map([...replace.entries()].sort((a, b) => a[0] - b[0])), add, addAt, missing };
}
// Same idea for a block column (ON-GROUND / BROADCAST / a platform): blocks
// sit on existing rows, so lines never create rows. "#N" lines go to row N;
// the rest go one per row from `at` down, skipping rows a "#N" line already
// took. Lines that run past the last row are reported, not placed.
//   place: Map(rowIndex -> lineIndex), overflow: line count past the end,
//   missing: "#N" row numbers that don't exist
function planBulkLane(parsed, rowCount, at) {
  const place = new Map();
  const missing = [];
  const free = [];
  (parsed || []).forEach((c, k) => {
    if (c.row) {
      if (c.row >= 1 && c.row <= rowCount) place.set(c.row - 1, k);
      else missing.push(c.row);
    } else free.push(k);
  });
  let p = Math.max(0, Math.min(Number(at) || 0, rowCount));
  let overflow = 0;
  for (const k of free) {
    while (p < rowCount && place.has(p)) p++;
    if (p < rowCount) { place.set(p, k); p++; } else overflow++;
  }
  return { place: new Map([...place.entries()].sort((a, b) => a[0] - b[0])), overflow, missing };
}
// Block-column placement by master cue NAME instead of row number. Each line
// is "ชื่อคิว<TAB>ข้อความ" (two columns copied from a sheet) or
// "ชื่อคิว = ข้อความ". Names are compared ignoring case, spaces and a leading
// "[time]"; an exact match wins, otherwise the first cue whose name contains
// the typed name (or vice versa). Names repeat in a rundown ("ยกที่ 1" every
// bout), so the search for each line starts just after the previous match —
// a list pasted in rundown order lands on the right occurrences.
//   place: Map(rowIndex -> lineIndex), text: Map(lineIndex -> text to place),
//   unmatched: names not found, noSep: count of lines without a separator
const normCueName = (s) => String(s || "").replace(/^\s*\[[^\]]*\]\s*/, "").replace(/\s+/g, "").toLowerCase();
function splitNamedLine(name) {
  const tab = name.indexOf("\t");
  if (tab !== -1) return [name.slice(0, tab).trim(), name.slice(tab + 1).trim()];
  const eq = name.indexOf("=");
  if (eq !== -1) return [name.slice(0, eq).trim(), name.slice(eq + 1).trim()];
  return null;
}
function planBulkLaneByName(parsed, masterCues) {
  const cues = (masterCues || []).map((m) => normCueName(m.name));
  const place = new Map();
  const text = new Map();
  const unmatched = [];
  let noSep = 0;
  let from = 0;
  (parsed || []).forEach((c, k) => {
    if (c.row) {
      if (c.row >= 1 && c.row <= cues.length) { place.set(c.row - 1, k); text.set(k, c.name); }
      else unmatched.push("#" + c.row);
      return;
    }
    const parts = splitNamedLine(c.name);
    if (!parts || !parts[0]) { noSep++; return; }
    const key = normCueName(parts[0]);
    const order = [...cues.keys()].slice(from).concat([...cues.keys()].slice(0, from));
    let hit = order.find((i) => cues[i] === key && !place.has(i));
    if (hit === undefined) hit = order.find((i) => cues[i] && !place.has(i) && (cues[i].includes(key) || key.includes(cues[i])));
    if (hit === undefined) { unmatched.push(parts[0]); return; }
    place.set(hit, k); text.set(k, parts[1]); from = hit + 1;
  });
  return { place: new Map([...place.entries()].sort((a, b) => a[0] - b[0])), text, unmatched, noSep };
}
// pulls a bout number out of typical rundown phrasing — "...คู่ที่ 1", "คู่ 2",
// or "Bout 3" — so pasting a real rundown auto-fills it instead of leaving
// every cue's bout number blank. Boxer names aren't detectable this way (the
// pasted text is action/procedure text, not a fight-card matchup), so those
// stay blank for the user to fill in per cue.
function detectBoutNumber(text) {
  const m = text.match(/คู่ที่\s*(\d+)/) || text.match(/คู่\s*(\d+)/) || text.match(/bout\s*#?\s*(\d+)/i);
  return m ? m[1] : "";
}

// longest-name-first substring match so a longer, more specific photo name
// wins over a shorter one that happens to also appear in the text.
function findPhotoMatch(text, photoList) {
  const norm = normalizePhotoName(text);
  if (!norm) return null;
  const sorted = [...(photoList || [])].filter((p) => p.name).sort((a, b) => b.name.length - a.name.length);
  return sorted.find((p) => norm.includes(normalizePhotoName(p.name))) || null;
}

function rwsMasterFromSep16() {
  const sat = WEEKDAYS.find((w) => w.id === "sat");
  return {
    ...emptyBoard(), name: sat.name, category: sat.category, code: sat.code,
    masterCues: RWS_16SEP_CUES.map((name) => ({ ...newMasterCue(), name, icon: guessIcon(name) })),
  };
}

function rwsMockupBoard() {
  const m = (name, icon) => ({ ...newMasterCue(), name, icon });
  const masterCues = [
    m("Sound Check", "mic"), m("ซ้อมคิวโอบี", "mic"), m("พิธีเปิดประตู", "corner"),
    m("VTR เปิดรายการ", "flame"), m("ประกาศคู่เอก", "gong"), m("เดินคู่ 1 เข้าเวที", "corner"),
    m("ยกที่ 1", "bell"), m("พักยก 1", "bell"), m("ยกที่ 2", "bell"), m("พักยก 2", "bell"),
    m("ยกที่ 3", "bell"), m("สรุปผล/มอบรางวัล", "belt"), m("VTR ปิดรายการ", "flame"), m("After Party", "flame"),
  ];
  const onGround = [
    { ...newBlock(0), image: "จอ LED เวทีเปิด", sound: "เพลงรอเปิดงาน" },
    { ...newBlock(1), image: "นักกีฬาซ้อมเดินเข้ามุม", sound: "" },
    { ...newBlock(2), image: "แฟนมวยเข้าสนาม", sound: "เพลงต้อนรับ" },
    { ...newBlock(5), image: "เดินคู่เอกเข้าสังเวียน", sound: "เพลงเดินคู่/กลองยาว" },
    { ...newBlock(6), image: "ไฟสปอตไลต์บนเวที", sound: "" },
    { ...newBlock(11), image: "ชูถ้วยรางวัลกลางเวที", sound: "เพลงชัยชนะ" },
    { ...newBlock(13), image: "เวทีโซนหลังเปิดไฟ", sound: "ดีเจ afterparty" },
  ];
  const broadcastMain = [
    { ...newBlock(3), image: "VTR เปิดรายการ RWS", sound: "เพลงเปิดรายการ" },
    { ...newBlock(4), image: "CG ชื่อคู่เอก", sound: "" },
    { ...newBlock(6), image: "Graphic คะแนนยกที่ 1", sound: "" },
    { ...newBlock(8), image: "Graphic คะแนนยกที่ 2", sound: "" },
    { ...newBlock(10), image: "Graphic คะแนนยกที่ 3", sound: "" },
    { ...newBlock(11), image: "CG ผลการแข่งขัน", sound: "" },
    { ...newBlock(12), image: "VTR สรุปไฮไลต์", sound: "เพลงซิกเนเจอร์ RWS" },
  ];
  const platforms = {
    YouTube: [ { ...newBlock(3), image: "Overlay โลโก้ YouTube", sound: "" }, { ...newBlock(11), image: "CG ชวนกดติดตาม", sound: "" } ],
    Facebook: [ { ...newBlock(3), image: "กราฟิกไลฟ์ Facebook", sound: "" } ],
  };
  return { name: "ราชดำเนินเวิลด์ซีรีส์", category: "RWS", code: "RWS", masterCues, onGround, broadcastMain, platforms };
}

export { STAGE_PRESET_BY_CODE, stageRows, dropCuesFromStage, stageFightStart, detectBouts, WEEKDAYS, weekdayOf, pad, toKey, fromKey, thaiDate, uid, newMasterCue, masterCueLabel, newBlock, newStage, stageCueIds, STAGE_PRESETS, STAGE_PRESET_CATEGORIES, findStagePreset, newStageFromPreset, emptyBoard, withBoardDefaults, getBlocksFromDay, computeRowHeights, cumulative, coveredRows, layoutBlocks, reindexAfterDelete, remapIds, RWS_16SEP_CUES, guessIcon, parseBulkCueLine, planBulkPaste, planBulkLane, planBulkLaneByName, detectBoutNumber, findPhotoMatch, rwsMasterFromSep16, rwsMockupBoard };
